import { useState, useEffect, useCallback, useMemo, useRef } from "react";

import { toast } from "sonner";
import {
  CreateThreadRequest,
  PersistentBrain,
  PersistentFile,
  PersistentMessage,
  PersistentThread,
} from "../sdk";
import {
  agentsGetWayakResponse,
  agentsGetSpecificAgentResponse,
  messagesCreateMessage,
  messagesDeleteMessage,
  messagesGetMessage,
  threadsCreateThread,
  threadsUploadThreadFile,
} from "../sdk";
import {
  compareMessagesForRender,
  convertSinglePersistentMessageToMessage,
} from "../messages/messages";
import { v4 as uuidv4 } from "uuid";
import { Message } from "../types/augmentations";
import {
  AvailableModel,
} from "../utils/models";
import { BACKEND_URL } from "../utils/config";
import { createClient as createSupabaseClient } from "@wayak/auth/utils/supabase/client";
import { useSSEStream } from "./use-sse-stream";
import { useMessages, useMessagesCache } from "../queries/messages";
import { useProjectFiles } from "../queries/projects";
export enum AgentChatType {
  WAYAK = "wayak",
  BRAIN = "brain",
}

/**
 * Props for the useAgentChat hook
 */
export interface UseAgentChatProps {
  initialMessages?: Message[];
  projectId?: string;
  userId?: string;
  initialThreadId?: string;
  orgId?: string;
  brains?: PersistentBrain[];
  agentType?: AgentChatType;
  models?: AvailableModel[];
  defaultModel?: string;
  hiddenSystemMessage?: string; // Hidden context message that doesn't get saved
}

/**
 * Return type for the useAgentChat hook
 */
export interface UseAgentChatReturn {
  messages: Message[];
  isLoading: boolean;
  isThinking: boolean;  // True when waiting for first token (agent is thinking)
  isPreparing: boolean; // True after tokens received, before tool_start or done
  sendMessage: (message: string, options?: { file?: File }) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  setModelWithLocalStorage: (model: string) => void;
  model: string;
  selectedModel?: AvailableModel;
  brains: PersistentBrain[];
  brain: PersistentBrain | null;
  setBrain: (brain: PersistentBrain | null) => void;
  projectFiles: PersistentFile[];
  setProjectFiles: React.Dispatch<React.SetStateAction<PersistentFile[]>>;
  ragFileIds: string[];
  setRagFileIds: React.Dispatch<React.SetStateAction<string[]>>;
  activeThreadId: string | null;
  setActiveThreadId: (threadId: string | null) => void;
  currentAgentType: AgentChatType;
  setCurrentAgentType: (agentType: AgentChatType) => void;
}

/**
 * A reusable hook for chat interactions with different agent types
 * (Wayak Agent, Brain)
 *
 * @param props - Configuration options for the hook
 * @returns Methods and state for chat interactions
 */
export function useAgentChat({
  initialMessages = [],
  userId,
  orgId,
  projectId,
  initialThreadId,
  brains = [],
  agentType = AgentChatType.WAYAK,
  models: initialModels = [],
  defaultModel = "",
  hiddenSystemMessage,
}: UseAgentChatProps): UseAgentChatReturn {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(
    initialThreadId || null
  );
  // Mirror of activeThreadId that callbacks can read synchronously. The
  // first send in a fresh chat creates the thread and immediately fires
  // pollMessageStatus / SSE onDone in the same async tick — React hasn't
  // committed the setActiveThreadId re-render yet, so any closure over
  // `activeThreadId` would still see `null`. Updating the ref the moment
  // the new thread id is known lets cache writes target the right key.
  const activeThreadIdRef = useRef<string | null>(initialThreadId || null);
  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  // Persisted messages from React Query cache (instant on back-navigation)
  const { data: cachedMessages = initialMessages || [] } = useMessages(activeThreadId);
  const { data: fetchedProjectFiles = [] } = useProjectFiles(projectId || "", {
    enabled: !!projectId,
  });

  // Streaming message (local state only — NOT in React Query cache)
  const [streamingMessage, setStreamingMessage] = useState<Message | null>(null);

  // Cache manipulation (write on onDone/delete only)
  const { upsertMessage, removeMessage } = useMessagesCache(activeThreadId);

  // Combined view: cached messages + active streaming message
  const messages = useMemo(() => {
    let merged: Message[];
    if (!streamingMessage) {
      merged = cachedMessages;
      return [...merged].sort(compareMessagesForRender);
    }
    // If streaming message ID already exists in cache, overlay it
    const idx = cachedMessages.findIndex((m) => m.id === streamingMessage.id);
    if (idx >= 0) {
      merged = cachedMessages.map((m) =>
        m.id === streamingMessage.id ? streamingMessage : m
      );
      return [...merged].sort(compareMessagesForRender);
    }
    merged = [...cachedMessages, streamingMessage];
    return [...merged].sort(compareMessagesForRender);
  }, [cachedMessages, streamingMessage]);
  const [model, setModel] = useState(defaultModel || "wayak");
  const [models, setModels] = useState<AvailableModel[]>(initialModels);
  const [projectFiles, setProjectFiles] = useState<PersistentFile[]>([]);
  const [ragFileIds, setRagFileIds] = useState<string[]>([]);
  const [brain, setBrain] = useState<PersistentBrain | null>(null);
  const [selectedModel, setSelectedModel] = useState(
    models.find((m) => m.model === model) || models[0]
  );
  const [currentAgentType, setCurrentAgentType] =
    useState<AgentChatType>(agentType);

  useEffect(() => {
    // `fetchedProjectFiles` comes from `useProjectFiles(projectId || "")` and
    // its `data: ... = []` destructuring default evaluates a fresh `[]` on
    // every render whenever the query is disabled (no projectId). That makes
    // this effect's deps churn on every render. Without bail-outs below,
    // `setProjectFiles([...])` commits a new array reference each time and
    // we trip React's max-update-depth limit on any chat surface that mounts
    // without a project — including the home chat page and any /threads/[id]
    // page. Compare by content first; only commit when something actually
    // changed.
    if (!projectId) {
      setProjectFiles((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const filtered = fetchedProjectFiles.filter((file) => {
      const isReady = file.index_status === "SUCCESS";
      const isRawTabular = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ].includes(file.file_type);
      return Boolean(file.id) && (isReady || isRawTabular);
    });

    setProjectFiles((prev) => {
      if (prev.length !== filtered.length) return filtered;
      for (let i = 0; i < prev.length; i++) {
        if (prev[i]?.id !== filtered[i]?.id) return filtered;
        if (prev[i]?.index_status !== filtered[i]?.index_status) return filtered;
      }
      return prev;
    });
  }, [fetchedProjectFiles, projectId]);

  // Refs for SSE stream accumulation (avoiding stale closure issues)
  const streamContentRef = useRef("");
  const streamStepsRef = useRef<Array<Record<string, unknown>>>([]);
  const streamMessageIdRef = useRef<string | null>(null);
  const streamCreatedAtRef = useRef<string | null>(null);

  // Unique instance ID to prevent polling race conditions across remounts
  const instanceIdRef = useRef(uuidv4());

  // Update the streaming message (local state — fast, no cache overhead)
  const updateStreamingMessage = useCallback((newMessage: Message) => {
    setStreamingMessage(newMessage);
  }, []);

  // Legacy appendMessage — used by polling path, writes to cache.
  // Targets the message's own thread_id when present, falling back to the
  // ref so freshly created threads still land in the right cache key.
  const appendMessage = useCallback((newMessage: Message) => {
    const targetThreadId =
      newMessage.thread_id ?? activeThreadIdRef.current ?? undefined;
    upsertMessage(newMessage, targetThreadId);
  }, [upsertMessage]);

  const finalizeErroredStreamingMessage = useCallback(
    async (messageId: string, fallbackError?: string) => {
      try {
        let persisted: PersistentMessage | null = null;

        for (let attempt = 0; attempt < 3; attempt++) {
          const response = await messagesGetMessage({
            path: { message_id: messageId },
          });
          persisted = response.data || null;

          if (
            persisted &&
            persisted.message_processing_status &&
            persisted.message_processing_status !== "PROCESSING"
          ) {
            break;
          }

          await new Promise((resolve) => setTimeout(resolve, 250));
        }

        if (persisted) {
          const converted = convertSinglePersistentMessageToMessage(persisted);
          if (converted) {
            const content =
              typeof converted.content === "string" ? converted.content.trim() : "";
            const normalizedMessage =
              !content && persisted.message_processing_status === "ERROR"
                ? {
                    ...converted,
                    content:
                      fallbackError ||
                      "Something went wrong finishing that response. Please try asking again — a more specific question usually helps.",
                  }
                : converted;
            upsertMessage(normalizedMessage);
          }
        }
      } catch (error) {
        console.error("Failed to rehydrate SSE message:", error);
        toast.error("Error processing message, please try again.");
      } finally {
        setStreamingMessage(null);
        streamContentRef.current = "";
        streamStepsRef.current = [];
        streamMessageIdRef.current = null;
        streamCreatedAtRef.current = null;
      }
    },
    [upsertMessage]
  );

  // Memoize SSE callbacks to avoid unnecessary object allocations
  // Note: Callbacks use refs internally to avoid stale closures
  // Pattern: onToken/onToolStart/onToolEnd → local state (fast)
  //          onDone → React Query cache (persists across navigation)
  const sseCallbacks = useMemo(
    () => ({
      onStart: (data: { message_id: string; status: string }) => {
        streamMessageIdRef.current = data.message_id;
        if (!streamCreatedAtRef.current) {
          streamCreatedAtRef.current = new Date().toISOString();
        }
      },
      onToken: (data: { content: string; message_id: string }) => {
        streamMessageIdRef.current = data.message_id;
        if (!streamCreatedAtRef.current) {
          streamCreatedAtRef.current = new Date().toISOString();
        }
        streamContentRef.current += data.content;
        // Local state only — no cache write per token
        updateStreamingMessage({
          role: "assistant",
          content: streamContentRef.current,
          id: data.message_id,
          intermediate_steps: streamStepsRef.current,
          created_at: streamCreatedAtRef.current,
        } as Message);
      },
      onToolStart: (data: {
        tool_name: string;
        tool_input: Record<string, unknown>;
        message_id: string;
        tool_call_id?: string | null;
      }) => {
        streamMessageIdRef.current = data.message_id;
        if (!streamCreatedAtRef.current) {
          streamCreatedAtRef.current = new Date().toISOString();
        }
        streamStepsRef.current = [
          ...streamStepsRef.current,
          {
            tool: data.tool_name,
            tool_name: data.tool_name,
            tool_call_id: data.tool_call_id ?? undefined,
            input: data.tool_input,
            status: "running",
            tool_calls: [
              {
                name: data.tool_name,
                args: data.tool_input,
                input: data.tool_input,
                id: data.tool_call_id ?? undefined,
              },
            ],
          },
        ];
        updateStreamingMessage({
          role: "assistant",
          content: streamContentRef.current,
          id: data.message_id,
          intermediate_steps: streamStepsRef.current,
          created_at: streamCreatedAtRef.current,
        } as Message);
      },
      onToolEnd: (data: {
        tool_name: string;
        tool_output: unknown;
        message_id: string;
        tool_call_id?: string | null;
      }) => {
        streamMessageIdRef.current = data.message_id;
        if (!streamCreatedAtRef.current) {
          streamCreatedAtRef.current = new Date().toISOString();
        }
        let matched = false;
        streamStepsRef.current = streamStepsRef.current.map((step) => {
          const stepToolCalls = Array.isArray(step.tool_calls)
            ? (step.tool_calls as Array<Record<string, unknown>>)
            : [];
          // Backend-emitted steps use `_status`, locally-appended steps use `status`.
          // Treat either flavor as the running marker so parallel tool ends still match.
          const isRunning =
            (step as Record<string, unknown>).status === "running" ||
            (step as Record<string, unknown>)._status === "running";
          const hasMatchingId =
            data.tool_call_id &&
            (step.tool_call_id === data.tool_call_id ||
              stepToolCalls[0]?.id === data.tool_call_id);
          const hasMatchingNameWithoutId =
            !data.tool_call_id &&
            (step.tool === data.tool_name || step.tool_name === data.tool_name) &&
            isRunning;

          if (!matched && isRunning && (hasMatchingId || hasMatchingNameWithoutId)) {
            matched = true;
            return {
              ...step,
              status: "completed",
              _status: "completed",
            };
          }

          return step;
        });
        updateStreamingMessage({
          role: "assistant",
          content: streamContentRef.current,
          id: data.message_id,
          intermediate_steps: streamStepsRef.current,
          created_at: streamCreatedAtRef.current,
        } as Message);
      },
      onStep: (data: { steps: Array<Record<string, unknown>>; message_id: string }) => {
        streamMessageIdRef.current = data.message_id;
        if (!streamCreatedAtRef.current) {
          streamCreatedAtRef.current = new Date().toISOString();
        }
        streamStepsRef.current = data.steps;
        updateStreamingMessage({
          role: "assistant",
          content: streamContentRef.current,
          id: data.message_id,
          intermediate_steps: streamStepsRef.current,
          created_at: streamCreatedAtRef.current,
        } as Message);
      },
      onContextCompression: (data: {
        message_id: string;
        original_tokens: number;
        compressed_tokens: number;
        messages_summarized: number;
        messages_kept: number;
        summary_preview: string;
      }) => {
        if (!streamCreatedAtRef.current) {
          streamCreatedAtRef.current = new Date().toISOString();
        }
        streamStepsRef.current = [
          {
            role: "system",
            type: "context_compression",
            original_tokens: data.original_tokens,
            compressed_tokens: data.compressed_tokens,
            messages_summarized: data.messages_summarized,
            messages_kept: data.messages_kept,
            summary_preview: data.summary_preview,
          },
          ...streamStepsRef.current,
        ];
        updateStreamingMessage({
          role: "assistant",
          content: streamContentRef.current,
          id: data.message_id,
          intermediate_steps: streamStepsRef.current,
          created_at: streamCreatedAtRef.current,
        } as Message);
      },
      onDone: (data: {
        message_id: string;
        final_content?: string;
        message_processing_status: "PROCESSING" | "SUCCESS" | "ERROR";
        task_outcome?: "answer" | "explore_with_context" | "clarify" | "act_from_context" | "safe_limit";
      }) => {
        streamMessageIdRef.current = data.message_id;
        const streamCreatedAt = streamCreatedAtRef.current || new Date().toISOString();
        const finalMessage = {
          role: "assistant",
          content: typeof data.final_content === "string" ? data.final_content : "",
          id: data.message_id,
          intermediate_steps: streamStepsRef.current,
          message_processing_status: data.message_processing_status,
          created_at: streamCreatedAt,
          edited_at: new Date().toISOString(),
          thread_id: activeThreadIdRef.current ?? undefined,
        } as Message;

        // Write finalized message to React Query cache (persists across navigation)
        upsertMessage(finalMessage);

        // Clear streaming state
        setStreamingMessage(null);
        streamContentRef.current = "";
        streamStepsRef.current = [];
        streamMessageIdRef.current = null;
        streamCreatedAtRef.current = null;
      },
      onError: (data: { error: string; message_id: string }) => {
        console.error("SSE Error:", data.error);
        void finalizeErroredStreamingMessage(data.message_id);
      },
      onConnectionError: (error: Error) => {
        console.warn("SSE Connection failed, will attempt polling fallback", error);
        if (streamMessageIdRef.current) {
          void finalizeErroredStreamingMessage(streamMessageIdRef.current);
          return;
        }
        setStreamingMessage(null);
        streamContentRef.current = "";
        streamStepsRef.current = [];
        streamMessageIdRef.current = null;
        streamCreatedAtRef.current = null;
      },
    }),
    [finalizeErroredStreamingMessage, updateStreamingMessage, upsertMessage]
  );

  // Fetch fresh auth headers for each SSE connection attempt (initial + retries)
  const getSSEHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const supabase = createSupabaseClient();
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return { Authorization: `Bearer ${session.access_token}` };
    }
    return {};
  }, []);

  // Configure SSE streaming hook with memoized callbacks
  const sseStream = useSSEStream({
    baseUrl: BACKEND_URL,
    getHeaders: getSSEHeaders,
    callbacks: sseCallbacks,
  });

  // Cleanup SSE stream and polling on unmount
  useEffect(() => {
    return () => {
      sseStream.stopStream();
      // Clear all active polling sessions for this instance
      activePollingRef.current.clear();
    };
  }, [sseStream.stopStream]);

  // Sync activeThreadId when initialThreadId prop changes
  useEffect(() => {
    if (initialThreadId && initialThreadId !== activeThreadId) {
      setActiveThreadId(initialThreadId);
    }
  }, [initialThreadId]);

  // Sync models from parent (useModelsWithLogos already handles fetching)
  useEffect(() => {
    if (initialModels.length > 0) {
      setModels(initialModels);
    }
  }, [initialModels]);

  // Load selected model from localStorage once on mount
  useEffect(() => {
    if (models.length === 0) return;
    const savedModel = localStorage.getItem("selectedModel");
    const resolved =
      (savedModel
        ? models.find((m) => m.model === savedModel) ||
          models.find((m) => m.name === savedModel) // legacy: stored by display name
        : undefined) ?? models[0];
    if (!resolved) return;
    // Only update state if the resolved model actually differs — avoids
    // a redundant re-render when the value is already correct (e.g. "wayak").
    if (resolved.model !== model) {
      setModel(resolved.model);
      setSelectedModel(resolved);
    }
    // Normalize localStorage to model ID
    localStorage.setItem("selectedModel", resolved.model);
  }, [models]);

  // Messages are now fetched by useMessages(activeThreadId) via React Query

  // Update agent type when brain changes
  useEffect(() => {
    if (brain) {
      setCurrentAgentType(AgentChatType.BRAIN);
    } else {
      setCurrentAgentType(AgentChatType.WAYAK);
    }
  }, [brain]);

  // Delete a message
  const deleteMessage = useCallback(async (messageId: string) => {
    try {
      await messagesDeleteMessage({
        path: { message_id: messageId },
      });
      // Remove from React Query cache
      removeMessage(messageId);
    } catch (error) {
      console.error("Failed to delete message:", error);
      toast.error("Failed to delete message, please try again.");
    }
  }, [removeMessage]);

  // Set model with localStorage persistence
  const setModelWithLocalStorage = useCallback(
    (modelValue: string) => {
      setModel(modelValue);
      localStorage.setItem("selectedModel", modelValue);
      const selectedModelContent = models.find((m) => m.model === modelValue);
      if (selectedModelContent) {
        setSelectedModel(selectedModelContent);
      }
    },
    [models]
  );

  // Get or create a thread
  const createThreadInternal = useCallback(
    async (messageContent: string): Promise<string | null> => {
      if (!userId) {
        console.error("User ID is required for thread creation");
        toast.error("Authentication required to create a conversation");
        return null;
      }

      try {
        const modelToUse = models.find((m) => m.model === model) || models[0];

        const createThreadData: CreateThreadRequest = {
          user_id: userId,
          title: messageContent.substring(0, 100), // Use first 100 chars of message as title
          description: "",
          project_id: projectId,
          org_id: orgId,
          model: modelToUse?.model || "",
        };

        const response = await threadsCreateThread({
          body: createThreadData,
        });

        if (!response.data || !response.data.id) {
          throw new Error("Failed to create thread");
        }

        // Update thread list. Mirror to the ref synchronously so callbacks
        // fired in the same tick (pollMessageStatus, SSE onDone) write to
        // the correct cache key before React commits the re-render.
        setActiveThreadId(response.data.id);
        activeThreadIdRef.current = response.data.id;
        return response.data.id;
      } catch (error) {
        console.error("Error creating thread:", error);
        toast.error("Failed to create conversation");
        return null;
      }
    },
    [userId, model, projectId, orgId, models]
  );

  // Prevent duplicate thread creation when concurrent sends happen before state updates.
  const pendingThreadCreationRef = useRef<Promise<string | null> | null>(null);

  // Map to track active polling sessions by instance ID (prevents race conditions across remounts)
  const activePollingRef = useRef<Map<string, boolean>>(new Map());

  // Poll for message status updates
  const pollMessageStatus = useCallback(
    async (messageId: string, interval = 2000): Promise<PersistentMessage> => {
      // Use instance ID to track this specific polling session
      const pollingId = `${instanceIdRef.current}:${messageId}`;
      activePollingRef.current.set(pollingId, true);

      try {
        while (activePollingRef.current.get(pollingId)) {
          const response = await messagesGetMessage({
            path: { message_id: messageId },
          });

          // Check if this polling session is still active after async operation
          if (!activePollingRef.current.get(pollingId)) {
            throw new Error("Polling aborted");
          }

          if (!response.data) {
            throw new Error("No data in response");
          }

          if (response.data.message_processing_status === "SUCCESS") {
            activePollingRef.current.delete(pollingId);
            return response.data;
          }

          if (response.data.message_processing_status === "ERROR") {
            activePollingRef.current.delete(pollingId);
            return response.data;
          }

          if (response.data.message_processing_status === "PROCESSING") {
            const convertedMessages = convertSinglePersistentMessageToMessage(
              response.data
            );

            if (convertedMessages) {
              appendMessage(convertedMessages);
            }
          }

          // Use a promise that can be interrupted
          await new Promise((resolve) => setTimeout(resolve, interval));
        }
        throw new Error("Polling aborted");
      } catch (error) {
        activePollingRef.current.delete(pollingId);
        throw error;
      }
    },
    [appendMessage]
  );

  // Save a message to the thread
  const saveMessage = useCallback(
    async (message: Message, thread_id: string) => {
      if (!thread_id) {
        console.error("Thread ID is required to save message");
        return;
      }

      const messageData = {
        role: message.role,
        content: {
          role: message.role,
          content: message.content,
        },
        thread_id: thread_id,
        models: message.models ?? [],
      };

      try {
        const response = await messagesCreateMessage({
          body: messageData,
        });
        return response.data || null;
      } catch (error) {
        console.error("Failed to send chat data:", error);
        return null;
      }
    },
    []
  );

  // Stream message content to create typing effect
  const streamMessageContent = useCallback(
    async (convertedMessages: Message) => {
      const content = convertedMessages.content;
      const chunkSize = 5; // Process multiple words at once for better performance
      const words = content.split(" ");
      const totalChunks = Math.ceil(words.length / chunkSize);

      for (let i = 1; i <= totalChunks; i++) {
        const partialContent = words.slice(0, i * chunkSize).join(" ");
        const partialMessage = {
          ...convertedMessages,
          content: partialContent,
        };
        appendMessage(partialMessage);

        // Reduced delay and only apply it for visible chunks
        if (i < totalChunks) {
          await new Promise((resolve) => setTimeout(resolve, 15));
        }
      }

      // Ensure the final message has the complete content
      appendMessage(convertedMessages);
    },
    [appendMessage]
  );

  // SSE streaming for Wayak agent — streams tokens in real-time
  const streamChatDataWithSSE = useCallback(
    async (
      messages: Message[],
      threadId: string,
      isFirstMessage: boolean = false
    ): Promise<boolean> => {
      let messagesToSend = messages;
      if (isFirstMessage && hiddenSystemMessage) {
        const hiddenMessage: Message = {
          role: "user",
          content: hiddenSystemMessage,
          id: "hidden-context",
        };
        messagesToSend = [hiddenMessage, ...messages];
      }

      // Filter out broken messages (empty or non-string content) before sending
      messagesToSend = messagesToSend.filter(
        (msg) => typeof msg.content === "string" && msg.content.trim().length > 0
      );

      const body = {
        messages: messagesToSend,
        threadId: threadId,
        model: model,
        projectId: projectId,
        orgId: orgId,
        fileIds: ragFileIds,
      };

      try {
        // Reset refs at start of each new stream
        streamContentRef.current = "";
        streamStepsRef.current = [];
        streamCreatedAtRef.current = null;

        // Auth headers are resolved by getHeaders on each connection attempt
        await sseStream.startStream(body);

        return true; // SSE handled successfully
      } catch (error) {
        console.warn("SSE: Connection failed, falling back to polling", error);
        return false;
      }
    },
    [model, projectId, orgId, ragFileIds, hiddenSystemMessage, sseStream]
  );

  // Send chat data to the appropriate service based on agent type
  const sendChatData = useCallback(
    async (
      messages: Message[],
      threadId: string,
      isFirstMessage: boolean = false
    ) => {
      // If this is the first message and we have a hidden system message, prepend it
      let messagesToSend = messages;
      if (isFirstMessage && hiddenSystemMessage) {
        const hiddenMessage: Message = {
          role: "user",
          content: hiddenSystemMessage,
          id: "hidden-context",
        };
        messagesToSend = [hiddenMessage, ...messages];
      }

      // Filter out broken messages (empty or non-string content) before sending
      messagesToSend = messagesToSend.filter(
        (msg) => typeof msg.content === "string" && msg.content.trim().length > 0
      );

      const body = {
        messages: messagesToSend,
        threadId: threadId,
        model: model,
        projectId: projectId,
        orgId: orgId,
        fileIds: ragFileIds,
      };

      let response: PersistentMessage | null = null;
      try {
        // For Wayak agent: try SSE streaming first, fall back to polling
        if (
          currentAgentType === AgentChatType.WAYAK ||
          (!currentAgentType && !brain)
        ) {
          const sseHandled = await streamChatDataWithSSE(
            messagesToSend,
            threadId,
            isFirstMessage
          );
          if (sseHandled) return; // SSE handled successfully
          // Fall through to polling if SSE failed
        }

        // Choose the right service based on agent type (polling path)
        let apiResponse;
        if (currentAgentType === AgentChatType.BRAIN && brain && brain.id) {
          apiResponse = await agentsGetSpecificAgentResponse({
            path: { agentId: brain.id },
            body: body,
          });
        } else {
          // Default to Wayak agent (polling fallback)
          apiResponse = await agentsGetWayakResponse({
            body: body,
          });
        }

        response = apiResponse.data || null;

        if (!response || !response.id) {
          console.error("Response ID not found");
          return;
        }

        // Poll for message status and stream the response
        const message = await pollMessageStatus(response.id);
        if (
          message.message_processing_status === "SUCCESS" ||
          message.message_processing_status === "ERROR"
        ) {
          const convertedMessages =
            convertSinglePersistentMessageToMessage(message);

          if (convertedMessages) {
            await streamMessageContent(convertedMessages);
          } else {
            throw new Error("Error processing message");
          }
          if (message.message_processing_status === "ERROR") {
            const renderedContent =
              typeof convertedMessages?.content === "string"
                ? convertedMessages.content.trim()
                : "";
            if (!renderedContent) {
              toast.error(
                "I couldn't finish that answer on this pass. Please retry and I'll continue from the same context."
              );
            }
          }
        } else {
          console.error("Message status:", message);
          toast.error("Error processing message, please try again.");
        }
      } catch (error) {
        console.error("Error processing message:", error);
        toast.error("Error processing message, please try again.");
      }
    },
    [
      currentAgentType,
      brain,
      model,
      projectId,
      orgId,
      ragFileIds,
      pollMessageStatus,
      streamMessageContent,
      streamChatDataWithSSE,
      hiddenSystemMessage,
    ]
  );

  const getOrCreateThread = useCallback(
    async (input: string) => {
      if (activeThreadId) {
        return activeThreadId;
      }

      if (pendingThreadCreationRef.current) {
        return pendingThreadCreationRef.current;
      }

      const pendingCreation = createThreadInternal(input);
      pendingThreadCreationRef.current = pendingCreation;

      try {
        return await pendingCreation;
      } finally {
        if (pendingThreadCreationRef.current === pendingCreation) {
          pendingThreadCreationRef.current = null;
        }
      }
    },
    [activeThreadId, createThreadInternal]
  );

  // Send a message (optionally with a file upload in a single atomic flow)
  const sendMessage = useCallback(
    async (input: string, options?: { file?: File }) => {
      setIsLoading(true);
      try {
        const threadId = await getOrCreateThread(input);

        if (!threadId) {
          setIsLoading(false);
          return;
        }

        // Upload file first if provided (same threadId, no race condition)
        if (options?.file) {
          const uploadResult = await threadsUploadThreadFile({
            path: { thread_id: threadId },
            body: { file: options.file },
          });
          if (uploadResult.error) {
            throw new Error(`File upload failed: ${JSON.stringify(uploadResult.error)}`);
          }
        }

        // Save user message

        const newMessage: Message = {
          role: "user",
          content: input,
          id: uuidv4(),
        };

        const saveMessageResult = await saveMessage(newMessage, threadId);
        console.log("saveMessageResult", saveMessageResult);

        if (saveMessageResult) {
          const convertedMessage =
            convertSinglePersistentMessageToMessage(saveMessageResult);
          if (convertedMessage) {
            // Write user message to React Query cache
            // Pass threadId explicitly to avoid stale closure when thread was just created
            upsertMessage(convertedMessage, threadId);
            const messagesToSend = [...messages, convertedMessage];
            // Check if this is the first message (only one message in the list)
            const isFirstMessage = cachedMessages.length === 0;
            await sendChatData(messagesToSend, threadId, isFirstMessage);
          }
        }
      } catch (error) {
        console.error("Error sending message:", error);
        toast.error("Failed to send message, please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [messages, cachedMessages, getOrCreateThread, saveMessage, upsertMessage, sendChatData]
  );

  // Return the hook's API
  return useMemo(
    () => ({
      messages: messages || [],
      isLoading,
      isThinking: sseStream.isThinking,
      isPreparing: sseStream.isPreparing,
      sendMessage,
      deleteMessage,
      setModelWithLocalStorage,
      model,
      selectedModel,
      brains: brains || [],
      brain,
      setBrain,
      projectFiles,
      setProjectFiles,
      ragFileIds,
      setRagFileIds,
      activeThreadId,
      setActiveThreadId,
      currentAgentType,
      setCurrentAgentType,
    }),
    [
      messages,
      isLoading,
      sseStream.isThinking,
      sseStream.isPreparing,
      sendMessage,
      deleteMessage,
      setModelWithLocalStorage,
      model,
      selectedModel,
      brains,
      brain,
      projectFiles,
      ragFileIds,
      activeThreadId,
      currentAgentType,
      setCurrentAgentType,
    ]
  );
}
